/**
 * Consent-gated PostHog bootstrap.
 *
 * Shopify's Customer Privacy API is the sole PostHog permission authority.
 * Missing API, load failure, thrown check, or false all fail closed.
 */
(function bootstrapPostHogConsent(globalObject) {
  'use strict';

  if (!globalObject?.document) return;

  const win = globalObject;
  const doc = win?.document;
  const shopify = win?.Shopify;
  const FEATURE = { name: 'consent-tracking-api', version: '0.1' };
  const READY_EVENT = 'snowcubes:posthog-ready';
  const OWNED_MARKER = '__SC_POSTHOG_OWNED';
  const config = win.__SC_POSTHOG_CONFIG;
  let initialized = false;
  let featureReady = false;

  function privacyApi() {
    const api = win?.Shopify?.customerPrivacy;
    return api && typeof api.analyticsProcessingAllowed === 'function'
      ? api
      : null;
  }

  function analyticsAllowed() {
    const api = privacyApi();
    if (!api) return false;
    try {
      return api.analyticsProcessingAllowed() === true;
    } catch (_error) {
      return false;
    }
  }

  function installPostHogStub() {
    if (win.posthog) return false;

    (function install(t, e) {
      let o;
      let n;
      let p;
      let r;
      if (e.__SV) return;
      win.posthog = e;
      e._i = [];
      e.init = function init(i, s, a) {
        function stub(target, method) {
          const parts = method.split('.');
          if (parts.length === 2) {
            target = target[parts[0]];
            method = parts[1];
          }
          target[method] = function queueCall() {
            target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
          };
        }
        p = t.createElement('script');
        p.type = 'text/javascript';
        p.crossOrigin = 'anonymous';
        p.async = true;
        p.src =
          s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') +
          '/static/array.js';
        // Do not announce analytics readiness while `posthog` is still the
        // pre-load stub. The storefront's event handlers otherwise capture
        // into the stub queue before the real SDK exists; when the SDK swaps
        // in, those interaction events are lost without a transport request.
        // The real PostHog bundle marks its global `__loaded` before the
        // script load event fires, so the caller can safely bind handlers and
        // capture the initial page/exposure against the live SDK.
        p.onload = () => {
          if (win.posthog?.__loaded !== true || !analyticsAllowed()) return;
          win.posthog.opt_in_capturing?.();
          announceReady();
        };
        r = t.getElementsByTagName('script')[0];
        r.parentNode.insertBefore(p, r);
        let target = e;
        if (a !== undefined) target = e[a] = [];
        else a = 'posthog';
        target.people = target.people || [];
        target.toString = function toString(mode) {
          let name = 'posthog';
          if (a !== 'posthog') name += `.${a}`;
          if (!mode) name += ' (stub)';
          return name;
        };
        target.people.toString = function peopleToString() {
          return target.toString(1) + '.people (stub)';
        };
        o =
          'init capture register register_once register_for_session unregister unregister_for_session captureException opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing get_explicit_consent_status is_capturing set_config get_distinct_id get_session_id'.split(
            ' '
          );
        for (n = 0; n < o.length; n += 1) stub(target, o[n]);
        e._i.push([i, s, a]);
      };
      e.__SV = 1;
    })(doc, win.posthog || []);
    win.posthog[OWNED_MARKER] = true;
    return true;
  }

  function announceReady() {
    win.__SC_POSTHOG_READY = true;
    const EventConstructor = win.CustomEvent || win.Event;
    if (typeof doc.dispatchEvent !== 'function' || typeof EventConstructor !== 'function') {
      return;
    }
    doc.dispatchEvent(new EventConstructor(READY_EVENT));
  }

  function announceReadyIfSdkLoaded() {
    if (!analyticsAllowed() || win.posthog?.__loaded !== true) return false;
    win.posthog.opt_in_capturing?.();
    announceReady();
    return true;
  }

  function initialize() {
    if (!analyticsAllowed()) return false;
    if (
      !config ||
      typeof config.key !== 'string' ||
      !/^phc_[A-Za-z0-9]{20,100}$/.test(config.key) ||
      config.host !== 'https://us.i.posthog.com'
    ) {
      return false;
    }
    if (initialized) {
      announceReadyIfSdkLoaded();
      return true;
    }
    if (!installPostHogStub()) return false;
    if (
      !win.posthog?.[OWNED_MARKER] ||
      typeof win.posthog?.init !== 'function'
    ) {
      return false;
    }

    win.posthog.init(config.key, {
      api_host: config.host,
      defaults: '2025-05-24',
      capture_pageview: false,
      person_profiles: 'identified_only',
      capture_performance: {
        web_vitals: true,
        web_vitals_allowed_metrics: ['CLS', 'FCP', 'INP', 'LCP'],
        network_timing: false,
      },
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
      },
      enable_recording_console_log: true,
      mask_personal_data_properties: true,
      custom_personal_data_properties: ['email', 'name', 'order', 'token'],
      opt_out_capturing_by_default: true,
      respect_dnt: true,
    });
    initialized = true;
    return true;
  }

  function revoke() {
    win.__SC_POSTHOG_READY = false;
    if (!initialized) return;
    try {
      win.posthog?.opt_out_capturing?.();
    } catch (_error) {
      // Analytics must never break the storefront.
    }
  }

  function syncConsent() {
    if (!featureReady) return;
    if (analyticsAllowed()) initialize();
    else revoke();
  }

  function start() {
    if (
      !doc ||
      !shopify ||
      typeof shopify.loadFeatures !== 'function' ||
      win.__SC_POSTHOG_CONSENT_LOADER_STARTED
    ) {
      return false;
    }

    win.__SC_POSTHOG_CONSENT_LOADER_STARTED = true;
    doc.addEventListener('visitorConsentCollected', syncConsent);
    shopify.loadFeatures([FEATURE], (error) => {
      if (error) return;
      featureReady = true;
      syncConsent();
    });
    return true;
  }

  start();
})(typeof window !== 'undefined' ? window : undefined);
